/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import Tbl from '@appblocks/codegeneration/Libraries/Tbl';
import TableItem from '@appblocks/codegeneration/SettingsConfigurations/TableItem';
import Cgg from '.';


export const getCodeBlocks = (feature: Cgg): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    const project: ProjectConfiguration = feature.project;
    if (!feature.getEnabled()) {
        return blocks;
    }

    const INTERFACE = 'current_interface';
    const TABLES = feature.getSettingValue('TABLES');
    const SERVER = feature.getSettingValue('SERVER');
    let SERVER_PORT = 1883;
    const settings = [];
    const CGG_MAX_SETTING_NAME_LENGTH = 0;
    const USERNAME = feature.getSettingValue('USERNAME');
    const PASSWORD = feature.getSettingValue('PASSWORD');
    const GROUPS = feature.getSettingValue('GROUPS');
    const DATA_TABLES = project.getFeature('tbl');
    const logEnabled = project.getSettingValue('log', 'ENABLED');
    const TABLES_ENABLED = DATA_TABLES && DATA_TABLES.getEnabled();
    const COMMANDSFeature = project.getFeature('commands');
    const appVersion = project.getSettingValue('general', 'FIRMWAREVERSION');
    const TLS_ENABLED = feature.getSettingValue('TLS', true) === '1';
    if (TLS_ENABLED) {
        SERVER_PORT = 8883;
    }
    let commands = [];
    if (COMMANDSFeature) {
        commands = COMMANDSFeature.getSettingValue('COMMANDS');
    }

    blocks.push({
        location: project.getNetworkConnectedLocation('current_interface'),
        blockType: BlockType.CODE,
        template: `
dim username as string=${USERNAME}
dim password as string=${PASSWORD}

if username <> "" and password <> "" then

    mqtt_start(current_interface,${SERVER},${SERVER_PORT},username,password,username,${TLS_ENABLED ? 'true' : 'false'})
    
end if`,
    });

    for (let i = 0; i < TABLES.length; i++) {
        const tblItem = project.getFeature(TABLES[i].getSettingValue('TABLE')) as TableItem;
        if (!tblItem) {
            continue;
        }
        const tableJson = Tbl.getTableJson(tblItem);
        blocks.push({
            location: 'CGG_CONSOLE_TABLES',
            blockType: BlockType.CODE,
            template: `
            ${JSON.stringify(tableJson)},`,
        });
    }

    const variables = project.getVariables();
    let variablesCode = '';
    let variableSetCode = '';
    let tablesSendCode = '';
    let commandsCode = '';
    const variableUpdateCodes: { [name: string]: string } = {};
    const enabledVariables = feature.getVariables();
    for (let i = 0; i < variables.length; i++) {
        const variable = variables[i];
        // only send update for variables in dashboard and settings
        const variableName = variable.getSettingValue('VARNAME');
        if (!enabledVariables.includes(variableName)) {
            continue;
        }
        let variableDatatype = variable.getSettingValue('DATATYPE');
        if (variableDatatype === TibboDataType.DATETIME) {
            variableDatatype = TibboDataType.DWORD;
        }
        if (variableDatatype === TibboDataType.TIME) {
            variableDatatype = TibboDataType.FLOAT;
        }
        const variableValue = project.convertDataType(
            variableName, variableDatatype, TibboDataType.STRING,
        );
        let variableString = `"+${variableValue}+"`;
        if (variableDatatype === TibboDataType.STRING) {
            variableString = `\\x22"+${variableValue}+"\\x22`;
        }
        const variableUpdateCode = `
mqtt_publish(cgg_topic_prefix+"/state/variables/${variableName}","{\\x22value\\x22:${variableString}}",QOS_AT_MOST_ONCE,false)`;
        variablesCode += variableUpdateCode;
        variableUpdateCodes[variableName] = variableUpdateCode;

        blocks.push({
            location: `var_${variableName}_changed`,
            blockType: BlockType.CODE,
            template: variableUpdateCode,
        });
        variableSetCode += `
    case "${variableName}":
        var_${variableName}_set(${project.convertDataType('value', TibboDataType.STRING, variableDatatype)})
        mqtt_publish(cgg_topic_prefix+"/state/variables/${variableName}","{\\x22value\\x22:${variableString}}",QOS_AT_MOST_ONCE,false)
        `;
    }

    for (let i = 0; i < commands.length; i++) {
        const command = commands[i];
        const commandName = command.getSettingValue('NAME');
        commandsCode += `
    case "${commandName}":
        cmd_${commandName}(cmd_data)`;
    }

    if (DATA_TABLES && DATA_TABLES.getEnabled()) {
        const TABLES = DATA_TABLES.getSettingValue('TABLES');
        for (let i = 0; i < TABLES.length; i++) {
            const table = TABLES[i];
            if (table.getSettingValue('STRUCT') === 'L') {
                continue;
            }
            const tableName = TABLES[i].getSettingValue('NAME');
            tablesSendCode += `
    cgg_send_table("${tableName}")
                `;
        }
    }

    blocks.push({
        location: 'callback_mqtt_connect_ok',
        blockType: BlockType.CODE,
        template: `
    cgg_lib.mqtt_buf=""
    cgg_vars_sent=false
    cgg_lib.mqtt_current_topic=cgg_topic_prefix+"/state/variables/+/set"
    mqtt_sub(cgg_lib.mqtt_current_topic,QOS_AT_MOST_ONCE)
    `,
    });

    blocks.push({
        location: 'callback_mqtt_sub_ok',
        blockType: BlockType.CODE,
        template: `
    if cgg_lib.mqtt_current_topic=cgg_topic_prefix+"/state/variables/+/set" then
        cgg_send_status()
        cgg_lib.mqtt_current_topic=cgg_topic_prefix+"/state/tables/+/changes"
        mqtt_sub(cgg_lib.mqtt_current_topic,QOS_AT_MOST_ONCE)
    else if cgg_lib.mqtt_current_topic=cgg_topic_prefix+"/state/tables/+/changes" then
        cgg_lib.mqtt_current_topic=cgg_topic_prefix+"/state/tables/+/import"
        mqtt_sub(cgg_lib.mqtt_current_topic,QOS_AT_MOST_ONCE)
        cgg_send_tables()
    else if cgg_lib.mqtt_current_topic=cgg_topic_prefix+"/state/tables/+/import" then
        romfile.open("cgg_console.json")
        mqtt_start_publish(cgg_topic_prefix+"/config",QOS_AT_MOST_ONCE,romfile.size, false)
        romfile.open("cgg_console.json")
        dim pointer_end as integer=romfile.size
        while romfile.pointer32<pointer_end
            mqtt_continue_publish(romfile.getdata(255))
        wend
        cgg_lib.mqtt_current_topic=""
        mqtt_sub(cgg_topic_prefix+"/commands/#",QOS_AT_MOST_ONCE)
        mqtt_sub(cgg_topic_prefix+"/firmware",QOS_AT_MOST_ONCE)
        mqtt_sub(cgg_topic_prefix+"/system/#",QOS_AT_MOST_ONCE)
        
    end if`,
    });

    blocks.push({
        location: 'callback_mqtt_notif',
        blockType: BlockType.CODE,
        template: `
    dim index as dword=1
    dim next_index as byte=instr(index,topic,"/",1)
    dim part as string=mid(topic,index,next_index-index)
    dim part_index as byte=0
    dim action as string=""
    dim tmp as string=""
    dim level as string="system"
    while next_index<>0
        select case part_index
            case 0: 'devices

            case 1: 'deviceid

            case 2:
                if part="state" then
                    level="state"
                else if part="commands" then
                    action=part
                else if part="firmware" then
                    if data<>"" AND upload_started=false then
                        'parse the url file name to extract firmware name, application, and version to determine whether needs upgrade
                        'extract the file name
                        dim tmp_index as byte=instr(1,data,"/",1)
                        while tmp_index<>0
                            index=tmp_index
                            tmp_index=instr(index+1,data,"/",1)
                        wend
                        dim file_name as string=mid(data,index+1,len(data)-index)
                        'extract the firmware name
                        dim app_id_str as string=mid(file_name,1,instr(1,file_name,"_",1)-1)
                        dim app_ver_str as string=mid(file_name,instr(1,file_name,"_",1)+1,instr(1,file_name,"_",2)-instr(1,file_name,"_",1)-1)
                        dim tios_ver_str as string=mid(file_name,instr(1,file_name,"_",2)+1,instr(1,file_name,".tcu",1)-instr(1,file_name,"_",2)-1)
                        if app_id_str=APP_ID AND app_ver_str=APP_VERSION AND tios_ver_str=TIOS_VERSION then
                            exit sub
                        end if

                        UPGRADE_URL=data
                        'workaround for g2 device without https capability
                        if instr(1,UPGRADE_URL,"https://",1)=1 then
                            UPGRADE_URL=mid(UPGRADE_URL,9,255)
                            UPGRADE_URL="http://" + UPGRADE_URL
                        end if
                        device_firmware_upload_async(PL_FW_UPG_WEB, http_request_item.socket)
                        http_request(HTTP_GET, UPGRADE_URL, current_interface,"")
                    end if
                end if
            case 3:
                if level="system" then
                    'commands
                    'execute command
                    dim cmd_data as string=json_get_value("data",data)
                    select case part
                        ${commandsCode}
                        case "I":
                            stg_restore_multiple(EN_STG_INIT_MODE_OVERRIDE)
                            cgg_vars_sent=false
                        case "B":
                            'buzz
                        case "E":
                            sys.reboot()
                    end select
                else
                    if part="variables" OR part="tables" then
                        action=part
                    end if
                end if
            case 4:
                if action="variables" OR action="tables" then
                    tmp=part
                end if
            case 5:
                if action="variables" AND part="set" then
                    'parse the json payload
                    dim name as string=tmp
                    dim value as string=json_get_value("data",data)
                    #IF MQTT_DEBUG_PRINT=1
                    sys.debugprint("CGG> set variable " + name + " to " + value + "\\r\\n")
                    #ENDIF
                    select case name
                        ${variableSetCode}

                    case else:
                        #IF MQTT_DEBUG_PRINT=1
                        sys.debugprint("CGG> unknown variable " + name + "\\r\\n")
                        #ENDIF
                    end select
                else if action="tables" AND part="changes" then
                    'parse the json payload
                    if data<>"" then
                    ${TABLES_ENABLED ? (`
                    dim table_name as string=tmp
                    dim change_type as string=json_get_value("action",data)
                    if change_type="add" then
                        dim row as string=json_get_value("data",data)
                        tbl_web_add_row(table_name,row)
                        #IF MQTT_DEBUG_PRINT=1
                        sys.debugprint("CGG> table " + table_name + " add " + data + "\\r\\n")
                        #ENDIF
                    else if change_type="remove" then
                        dim row as string=val(json_get_value("row",data))
                        #IF MQTT_DEBUG_PRINT=1
                        sys.debugprint("CGG> table " + table_name + " remove " + data + "\\r\\n")
                        #ENDIF
                        tbl_web_delete_row(table_name,row)
                    else if change_type="update" then
                        #IF MQTT_DEBUG_PRINT=1
                        sys.debugprint("CGG> table " + table_name + " update " + data + "\\r\\n")
                        #ENDIF
                        dim row_data as string=json_get_value("data",data)
                        dim row as word=val(json_get_value("row",data))
                        tbl_web_edit_row(table_name,row,row_data)
                    else if change_type="clear" then
                        #IF MQTT_DEBUG_PRINT=1
                        sys.debugprint("CGG> table " + table_name + " clear  " + "\\r\\n")
                        #ENDIF
                        tbl_web_clear_table(table_name)
                    end if
                    'clear the changes
                    mqtt_publish(cgg_topic_prefix+"/state/tables/" + table_name + "/changes","",QOS_AT_MOST_ONCE,false)
                    cgg_send_table(table_name)`) : ''}
                    end if

                else if action="tables" AND part="import" then
                    dim table_name as string=tmp
                    index=0
                    dim line_index as byte=0
                    dim buf as string=""
                    if len(data)+remaining_bytes=mqtt_proc_data_variables.total_length then
                        tbl_web_clear_table(table_name)
                    else
                        tmp=data
                        data=cgg_lib.mqtt_buf
                        cgg_lib.mqtt_buf=tmp
                        line_index=1
                    end if
                    dim current_line as string=""
                    dim total_len as word=len(data)+len(cgg_lib.mqtt_buf)
                    while index<total_len
                        dim c as string(1)=mid(data,1,1)
                        data=mid(data,2,len(data))
                        index=index+1
                        if c<>"\\n" AND index<=total_len then
                            buf=buf+c
                        end if
                        if c="\\n" OR (index=total_len AND remaining_bytes=0) then
                            if line_index<>0 then
                                'add row to table
                                tbl_web_add_row(table_name,buf)
                            end if
                            buf=""
                            line_index=line_index+1
                        end if
                        if len(data)=0 AND index<total_len then
                            data=cgg_lib.mqtt_buf
                            cgg_lib.mqtt_buf=""
                        end if
                    wend
                    cgg_lib.mqtt_buf=buf
                    'clear the import
                    if remaining_bytes=0 then
                        mqtt_publish(cgg_topic_prefix+"/state/tables/" + table_name + "/import","",QOS_AT_MOST_ONCE,true)
                    else
                        cgg_tbl_changed=false 'postpone sync until all data is received
                    end if

                end if
            case else:

        
        end select
        if next_index>len(topic) then
            exit while
        end if
        index=next_index+1
        next_index=instr(index,topic,"/",1)
        if next_index=0 then
            next_index=len(topic)+1
        end if
        part=mid(topic,index,next_index-index)
        part_index=part_index+1
    wend
        `,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
type cgg_lib_type
    mqtt_buf as string
    mqtt_current_topic as string
end type
declare sub cgg_send_status()
declare cgg_push_timer as dword
declare sub cgg_send_tables()
declare sub cgg_send_table(table as string)
declare sub cgg_proc_timer()
declare cgg_topic_prefix as string
declare cgg_tbl_changed as boolean
declare cgg_log_pending as boolean
declare cgg_next_timestamp as dword
declare cgg_vars_sent as boolean
declare cgg_lib as cgg_lib_type
`,
    });

    blocks.push({
        location: 'GLOBAL_DIMS',
        blockType: BlockType.CODE,
        template: `
dim cgg_push_timer as dword=0
dim cgg_topic_prefix as string=""
dim cgg_tbl_changed as boolean=false
dim cgg_log_pending as boolean=true
dim cgg_next_timestamp as dword=0
dim cgg_vars_sent as boolean=false
dim cgg_lib as cgg_lib_type
`,
    });

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
cgg_topic_prefix="devices/" + ${USERNAME}
`,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.CODE,
        template: `
sub cgg_send_status()
    dim payload as string=""
    dim firmwareVersion as string=mid(sys.version, 2,len(sys.version) - 2)
    dim timestamp as dword=datetime_current_timestamp()
    dim daycount, mincount as word
    dim seconds as byte
    datetime_from_timestamp(timestamp, daycount, mincount, seconds)
    rtc.getdata(daycount, mincount, seconds)
    payload = "{"
    payload = payload + "\\x22firmwareVersion\\x22:\\x22" + firmwareVersion + "\\x22,"
    payload = payload + "\\x22ip\\x22:\\x22" + net.ip + "\\x22,"
    payload = payload + "\\x22mac\\x22:\\x22" + hex_mac(net.mac) + "\\x22,"
    payload = payload + "\\x22bootTime\\x22:\\x22" + lstr(timestamp - sys.timercount32 / 2)  + "\\x22,"
    payload = payload + "\\x22wifiOn\\x22:\\x22" + str(wln.enabled) + "\\x22,"
    payload = payload + "\\x22timezone\\x22:\\x22" + str(APP_TIMEZONE) + "\\x22,"
    payload = payload + "\\x22time\\x22:\\x22" + lstr(timestamp) + "\\x22"
    
    payload = payload + "}"
    mqtt_publish("devices/" + ${USERNAME} + "/state",payload,QOS_AT_MOST_ONCE,false)

    mqtt_publish("devices/" + ${USERNAME} + "/state/appName","${project.name}",QOS_AT_MOST_ONCE,false)
    mqtt_publish("devices/" + ${USERNAME} + "/state/appId","${project.id}",QOS_AT_MOST_ONCE,false)
    mqtt_publish("devices/" + ${USERNAME} + "/state/appVersion","${appVersion}",QOS_AT_MOST_ONCE,false)
end sub

sub cgg_send_table(table as string)
#IFDEF TBL_MAX_TOTAL_NUM_FIELDS
    dim i as word
    dim j, num_of_fld as byte
    dim tbl_rows, tbl_rows_all as word
    dim tmp_data as string=""
    dim header as string=""
    dim length as word=0
    dim topic as string=cgg_topic_prefix+"/state/tables/"+table

    tbl_web_get_table_active_rows(table, tbl_rows, tbl_rows_all)

    num_of_fld = tbl_get_num_fields(table)


    header=tbl_web_get_header(table)+"\\r\\n"

    length = length + len(header)

    for i = 1 to tbl_rows_all
        if tbl_rows_all=0 then
            exit for
        end if
        if tbl_is_record_deleted(i)<>YES then
            tmp_data=tbl_web_get_fld_data(i, num_of_fld)+"\\r\\n"
            length = length + len(tmp_data)
            tmp_data=""
        end if
    next i
    mqtt_start_publish(topic,QOS_AT_MOST_ONCE,length, false)
    tmp_data = header
    mqtt_continue_publish(tmp_data)
    for i = 1 to tbl_rows_all
        if tbl_rows_all=0 then
            exit for
        end if
        if tbl_is_record_deleted(i)<>YES then
            tmp_data=tbl_web_get_fld_data(i, num_of_fld)+"\\r\\n"
            mqtt_continue_publish(tmp_data)
            tmp_data=""
        end if
    next i
#ENDIF
end sub

sub cgg_send_tables()
    ${tablesSendCode}
end sub

sub cgg_proc_timer()
    '****cgg_proc_timer****'
    if cgg_tbl_changed=true AND mqtt_state=EN_MQTT_STATES_LOGGED_IN then
        cgg_send_tables()
        cgg_tbl_changed=false
    end if
    if sys.timercountms < cgg_next_timestamp then
        exit sub
    end if
    cgg_next_timestamp=sys.timercountms+500

    if mqtt_state=EN_MQTT_STATES_LOGGED_IN then
        if cgg_vars_sent=false then
        ${variablesCode}
        cgg_vars_sent=true
        end if

        if cgg_log_pending=true then

        #IFDEF TBL_MAX_TOTAL_NUM_FIELDS
        dim i as word
        dim j, num_of_fld as byte
        dim tbl_rows,debug_rows as word
        dim tmp_data as string=""
        dim topic as string=cgg_topic_prefix+"/logs"
        dim fld_data as string=""
        
        dim tbl_select_status as en_tbl_status_codes
        ${logEnabled ? (`
        

            tbl_select_status = tbl_select("LOG","LOG")

            if tbl_select_status <> EN_TBL_STATUS_OK then
                exit sub
            end if

            tbl_get_num_records(tbl_rows,NO)
            if tbl_rows>0 then
                dim key_data as string=""
                dim evt_data as string=""

                tbl_record_read_active(1)
                fld_data=tbl_field_get("timestamp")
                tmp_data="{\\x22createdAt\\x22:"+fld_data+",\\x22data\\x22:{"
                'go through all the items in the Event field
                fld_data=tbl_field_get("value")
                tbl_record_delete(1)
                if fld_data<>"" then
                    i=0
                    while i < len(fld_data)
                        i=i+1
                        j=instr(i,fld_data,"=",1)
                        if j=0 then
                            exit while
                        end if
                        tmp_data=tmp_data + "\\x22"+mid(fld_data,i,j-i)+"\\x22:"
                        i=j+1
                        j=instr(i,fld_data,";",1)
                        if j=0 then
                            exit while
                        end if
                        tmp_data=tmp_data + "\\x22"+mid(fld_data,i,j-i)+"\\x22"
                        i=j
                        if i<len(fld_data) then
                            tmp_data=tmp_data + ","
                        end if
                    wend
                    tmp_data=tmp_data+"},"
                    tmp_data=tmp_data+"\\x22type\\x22:\\x22event\\x22"
                    tmp_data=tmp_data+"}"
                    mqtt_publish(topic,tmp_data,QOS_AT_MOST_ONCE,false)
                end if
            end if
            `) : ''}

            #IFDEF RETARGET_DEBUG_PRINT
            tbl_select_status = tbl_select("DEBUG","DEBUG")

            if tbl_select_status <> EN_TBL_STATUS_OK then
                exit sub
            end if

            tbl_get_num_records(debug_rows,NO)
            if debug_rows>0 then
                tbl_record_read_active(1)
                fld_data=tbl_field_get("timestamp")
                tmp_data="{\\x22createdAt\\x22:"+fld_data+",\\x22data\\x22:\\x22"
                fld_data=tbl_field_get("value")
                tbl_record_delete(1)
                'not MQTT publish message
                if instr(1,fld_data,"MQTT> Publishing:",1)=0 AND fld_data<>"" then
                    fld_data=escape_string(fld_data)
                    tmp_data=tmp_data+fld_data+"\\x22"
                    tmp_data=tmp_data+",\\x22type\\x22:\\x22debug\\x22"
                    tmp_data=tmp_data+"}"
                    mqtt_publish(topic,tmp_data,QOS_AT_MOST_ONCE,false)
                end if
            end if
            #ENDIF


            if tbl_rows=0 AND debug_rows=0 then
                cgg_log_pending=false
            end if

            #ENDIF

        end if

    end if
end sub
`,
    });

    let variableGetCode = '';
    const variablesGetCode = [];
    let cmdExecCode = '';
    const widgets = feature.getSettingValue('WIDGETS');
    const widgetsArray = [];
    blocks.push({
        location: 'CGG_SETUP_TITLE',
        blockType: BlockType.LOCATION,
        template: `
        "title": "Device Console"`,
    });
    if (widgets.length > 0) {
        const variableInCode: { [variable: string]: boolean } = {};
        for (let i = 0; i < widgets.length; i++) {
            const widget = widgets[i].getWidgetObject();
            let { variable, linkType } = widget;
            try {
                const item = project.getFeature(variable);
                if (item?.id === 'variable') {
                    variable = item.getSettingValue('VARNAME');
                    const varValue = project.convertDataType(variable, item.getSettingValue('DATATYPE'), TibboDataType.STRING);
                    linkType = 'variable';
                    if (!Object.prototype.hasOwnProperty
                        .call(variableInCode, variable)) {
                        variableGetCode += `
        case "${variable}":
            var_value= ${varValue}`;
                        variableSetCode += `
        case "${variable}":
            var_${variable}_set(${project.convertDataType('value', TibboDataType.STRING, item.getSettingValue('DATATYPE'))})`;
                        variableInCode[variable] = true;
                        variablesGetCode.push(`sock.setdata("\\x22${variable}\\x22:" + ${varValue})`);
                        variablesGetCode.push('sock.setdata(",")');
                    }
                } else if (item?.id === 'command') {
                    variable = item.getSettingValue('NAME');
                    linkType = 'command';
                    if (!Object.prototype.hasOwnProperty
                        .call(variableInCode, variable)) {
                        cmdExecCode += `
        case "${variable}":
            cmd_${variable}(value)`;
                        variableInCode[variable] = true;
                    }
                }
                widget.variable = variable;
                widget.linkType = linkType;
                widgetsArray.push(widget);
            } catch (ex) {
                widgets.splice(i, 1);
                i--;
            }
        }
    }

    blocks.push({
        location: 'CGG_CONSOLE_SETUP_WIDGETS',
        blockType: BlockType.CODE,
        template: `
        "updateInterval": ${feature.getSettingValue('INTERVAL') * 1000},
        "widgets": ${JSON.stringify(widgetsArray)},`,
    });

    blocks.push({
        location: 'CGG_APP_VERSION',
        blockType: BlockType.CODE,
        template: `
        "appName": "${project.name}",
        "appVersion": "${appVersion}",
        "appId": "${project.id}",
        `,
    });

    let settingsCode = '';
    for (let i = 0; i < GROUPS.length; i++) {
        const tmpBlocks = GROUPS[i].getCodeBlocks();
        for (let j = 0; j < tmpBlocks.length; j++) {
            if (tmpBlocks[j].location === 'LUIS_GROUP_ITEM') {
                settingsCode += tmpBlocks[j].template;
                if (i + 1 < GROUPS.length) {
                    settingsCode += ',';
                }
            }
        }
    }

    blocks.push({
        location: 'callback_tbl_modified',
        blockType: BlockType.LOCATION,
        template: `
    dim table_metadata as tbl_type
    tbl_get_table_info(file_name, table_metadata)
    if table_metadata.structure=EN_TBL_STRUCT_TABLE then
        'table changed
        cgg_tbl_changed=true
    else
        'log changed
        cgg_log_pending=true
    end if
    `,
    });

    blocks.push({
        location: 'on_sys_timer',
        blockType: BlockType.LOCATION,
        template: `
    cgg_proc_timer()`,
    });

    blocks.push({
        location: 'CGG_SETTINGS_SCHEMA',
        template: settingsCode,
    });

    return blocks;
};
